import { useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import { momentFormat } from "../lib/dateFormat";
import BetStatusBadge from "../common/BetStatusBadge";
import { getLegDisplayStatus } from "../lib/betLifecycleStatus";
import { formatMoney, getTicketFinancials } from "../lib/wagerFinancials";
import { formatOdds } from "../../utility/odds";

function LegStatusLabel({ leg }) {
  const { label, variant } = getLegDisplayStatus(leg);
  return (
    <span className={`bet-status-badge bet-status-${variant}`}>{label}</span>
  );
}

function WagerModal({ show, handleClose, item }) {
  const financials = useMemo(() => getTicketFinancials(item), [item]);

  const showPtsPurchased = item?.betType === "Straight";

  const ptsPurchased = useMemo(() => {
    if (!item || !showPtsPurchased) return "0.00";
    return formatMoney(item.buyPoints ?? 0);
  }, [item, showPtsPurchased]);

  if (!item) return null;

  return (
    <div className="wager_popup">
      <Modal
        show={show}
        onHide={handleClose}
        centered
        className="wager_modal"
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            WAGER DETAILS FOR TICKET #{item.ticketId}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3 px-1">
            <span className="heading d-block mb-2">Ticket status</span>
            <BetStatusBadge item={item} />
          </div>
          <div className="row wager_details">
            <div className="col-xl-6 cols">
              <div className="row">
                <div className="col-4">
                  <span className="heading">Wager Type</span>
                </div>
                <div className="col-8">
                  <input
                    type="text"
                    className="content"
                    readOnly
                    value={item.betType}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-6 cols">
              <div className="row">
                <div className="col-4">
                  <span className="heading">Date Placed</span>
                </div>
                <div className="col-8">
                  <input
                    type="text"
                    className="content"
                    readOnly
                    value={momentFormat(item.createdAt)}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-6 cols">
              <div className="row">
                <div className="col-4">
                  <span className="heading">Settled Date</span>
                </div>
                <div className="col-8">
                  <input
                    type="text"
                    className="content"
                    readOnly
                    value={momentFormat(item.updatedAt)}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-6 cols">
              <div className="row">
                <div className="col-4">
                  <span className="heading">Risk Amount $</span>
                </div>
                <div className="col-8">
                  <input
                    type="text"
                    className="content"
                    readOnly
                    value={formatMoney(financials.riskAmount)}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-6 cols">
              <div className="row">
                <div className="col-4">
                  <span className="heading" title="Win return (payout minus risk).">
                    Win Amount $
                  </span>
                </div>
                <div className="col-8">
                  <input
                    type="text"
                    className="content"
                    readOnly
                    value={formatMoney(financials.winAmount)}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-6 cols">
              <div className="row">
                <div className="col-4">
                  <span className="heading" title="Amount returned if ticket wins.">
                    Total Payout $
                  </span>
                </div>
                <div className="col-8">
                  <input
                    type="text"
                    className="content"
                    readOnly
                    value={formatMoney(financials.totalPayout)}
                  />
                </div>
              </div>
            </div>

            {showPtsPurchased && (
              <div className="col-xl-6 cols">
                <div className="row">
                  <div className="col-4">
                    <span
                      className="heading"
                      title="Half-points purchased on this straight bet."
                    >
                      PTS Purchased
                    </span>
                  </div>
                  <div className="col-8">
                    <input
                      type="text"
                      className="content"
                      readOnly
                      value={ptsPurchased}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="table_data mt-3">
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Details</th>
                  <th>Status</th>
                  <th className="game_date">Game Date</th>
                  <th>Teams</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {item.betType === "Straight" ? (
                  <tr>
                    <td>
                      {item.teamName}
                      <br />
                      Price {formatOdds(item.price)}
                      <br />
                      {item.point ? item.point : ""}
                      <br />
                      {item.pointType}
                    </td>
                    <td>
                      <LegStatusLabel leg={item} />
                    </td>
                    <td>{momentFormat(item.gameTime)}</td>
                    <td>
                      {item.homeTeam}
                      <br />
                      {item.awayTeam}
                    </td>
                    <td>
                      <p>{item.score1}</p>
                      <p>{item.score2}</p>
                    </td>
                  </tr>
                ) : (
                  item.bettingData &&
                  item.bettingData.map((bet, i) => (
                    <tr key={i}>
                      <td>
                        {bet.teamName}
                        <br />
                        Price {formatOdds(bet.price)}
                        <br />
                        {bet.point ? bet.point : ""}
                        <br />
                        {bet.pointType}
                      </td>
                      <td>
                        <LegStatusLabel leg={bet} />
                      </td>
                      <td>{momentFormat(bet.gameTime)}</td>
                      <td>
                        {bet.homeTeam}
                        <br />
                        {bet.awayTeam}
                      </td>
                      <td>
                        <p>{bet.score1}</p>
                        <p>{bet.score2}</p>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn img_btn getbtn close_btn cursor_pointer"
            onClick={handleClose}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default WagerModal;
